import { graphql } from 'gatsby';
import React, { Suspense, useEffect } from 'react';

import NobleSection from '../components/Common/NobleSection';
import RotatingMedia from '../components/Common/RotatingMedia';
import Cards from '../components/Common/SideScroll/Cards';
import ManualSideScroll from '../components/Common/SideScroll/ManualSideScroll';
import StylizedHeadlineCallout from '../components/Common/StylizedHeadlineCallout';
import TabbedContent from '../components/Common/TabbedContent';
import TwoColumnImageWithCards from '../components/Common/TwoColumnImageWithCards';
import StylizedHeadlineHeader from '../components/Heros/StylizedHeadlineHeader';
import Accordion from '../components/HomePage/Accordion';
import BehavioralDataCallout from '../components/HomePage/BehavioralDataCallout';
import CustomerInFocus from '../components/HomePage/CustomerInFocus/CustomerInFocus';
import VideoBanner from '../components/HomePage/VideoBanner';
import VideoCallout from '../components/HomePage/VideoCallout';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import AdditionalResources from '../components/PageSections/AdditionalResources';
import PageSection from '../components/PageSections/PageSection';
import DelightHint from '../components/ScavengerHunt/Hints/DelightHint';
import * as styles from './homepageModular.module.scss';

const FloatingCalloutLazy = React.lazy(() => import('../components/Common/FloatingCallout'));
const RotatingMediaLazy = React.lazy(() => import('../components/Common/RotatingMedia'));
const BehavioralDataCalloutLazy = React.lazy(() => import('../components/HomePage/BehavioralDataCallout'));
const CustomerInFocusLazy = React.lazy(() => import('../components/HomePage/CustomerInFocus/CustomerInFocus'));
const AdditionalResourcesLazy = React.lazy(() => import('../components/PageSections/AdditionalResources'));

export default function HomepageModular({ data: { page } }) {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window['_fs_run_in_iframe'] = true;
    }
    return () => {
      if (typeof window !== 'undefined') {
        window['_fs_run_in_iframe'] = false;
      }
    };
  });

  const sections = page.pageSections.map((section, i) => {
    switch (section.__typename) {
      case 'ContentfulSectionTabbedContent': {
        return (
          <TabbedContent
            key={i}
            heading={section.mainHeading}
            lede={section.lede?.lede}
            content={section.tabbedContent}
          />
        );
      }

      case 'ContentfulComponentMediaGroup': {
        return (
          <React.Fragment key={i}>
            <PageSection key={i} slim className={styles.customerLogos}>
              {i > 1 ? (
                <Suspense>
                  <RotatingMediaLazy section={section} inline={true} />
                </Suspense>
              ) : (
                <RotatingMedia section={section} inline={true} />
              )}
            </PageSection>
            <NobleSection />
          </React.Fragment>
        );
      }
      case 'ContentfulSectionGroupedItems': {
        if (section.type === 'Accordion') {
          return <Accordion key={i} section={section} />;
        } else if (section.type === 'Two Column Image with Cards') {
          return <TwoColumnImageWithCards key={i} section={section} />;
        } else if (section.type === 'Side Scroll') {
          return (
            <ManualSideScroll key={i}>
              <DelightHint />
              <Cards content={section.content} cardIsClickable={false} />
            </ManualSideScroll>
          );
        } else {
          return i > 1 ? (
            <Suspense key={i}>
              <BehavioralDataCalloutLazy section={section} />
            </Suspense>
          ) : (
            <BehavioralDataCallout key={i} section={section} />
          );
        }
      }
      case 'ContentfulSectionContentSection': {
        if (section.useFloatingLayout) {
          return i > 1 ? (
            <Suspense key={i}>
              <FloatingCalloutLazy section={section} />
            </Suspense>
          ) : (
            <div className={styles.videoCallout} key={i}>
              <VideoCallout section={section} />
              <VideoBanner colorTheme={'Pink 25% Tint'} offset={'0'} />
            </div>
          );
        } else {
          return i > 1 ? (
            <Suspense key={i}>
              <CustomerInFocusLazy section={section} />
            </Suspense>
          ) : (
            <CustomerInFocus key={i} section={section} />
          );
        }
      }
      case 'ContentfulSectionAdditionalResources': {
        return i > 1 ? (
          <Suspense key={i}>
            <AdditionalResourcesLazy section={section} />
          </Suspense>
        ) : (
          <AdditionalResources key={i} section={section} />
        );
      }
      case 'ContentfulSectionStylizedHeadlineCallout': {
        return <StylizedHeadlineCallout key={i} section={section} />;
      }
      default:
        return null;
    }
  });

  return (
    <Layout pageMeta={page.pageMetaData}>
      <StylizedHeadlineHeader fields={page.header} />
      {sections}
    </Layout>
  );
}

export const Head = ({ data: { page }, location }) => (
  <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
);

export const query = graphql`
  query HomepageModularPageQuery {
    page: contentfulPageStandard(contentful_id: { eq: "6jYDluRIDHx1o8UH3Gs2jt" }) {
      ...StandardPage
    }
  }
`;
